import { db } from './db';

type EntityAction =
  | 'create_equipment'
  | 'update_equipment'
  | 'delete_equipment'
  | 'create_local'
  | 'update_local'
  | 'delete_local';

class EntitySyncService {
  private isSyncing = false;

  async queueAction(type: EntityAction, payload: Record<string, unknown>, entityId?: string) {
    if (!import.meta.client) return;

    await db.syncQueue.add({
      id: crypto.randomUUID(),
      type,
      entityId,
      payload: { data: payload },
      status: 'pending',
      retries: 0,
      createdAt: Date.now(),
      updatedAt: Date.now()
    });
  }

  async syncPending() {
    if (!import.meta.client || !navigator.onLine) return;
    if (this.isSyncing) return;

    this.isSyncing = true;
    try {
      const actionTypes: EntityAction[] = [
        'create_equipment',
        'update_equipment',
        'delete_equipment',
        'create_local',
        'update_local',
        'delete_local'
      ];

      const pendingTasks = await db.syncQueue
        .where('status')
        .anyOf('pending', 'error')
        .filter((t) => actionTypes.includes(t.type as EntityAction))
        .toArray();

      pendingTasks.sort((a, b) => a.createdAt - b.createdAt);

      for (const task of pendingTasks) {
        try {
          await db.syncQueue.update(task.id, { status: 'syncing', updatedAt: Date.now() });

          const payloadData = JSON.parse(
            JSON.stringify('data' in task.payload ? task.payload.data : task.payload)
          ) as Record<string, unknown>;

          if (
            task.type.includes('equipment') &&
            payloadData &&
            typeof payloadData === 'object' &&
            'image' in payloadData &&
            typeof payloadData.image === 'string' &&
            payloadData.image.startsWith('offline://')
          ) {
            const attachmentId = payloadData.image.replace('offline://', '');
            const offlineAttachment = await db.offlineAttachments.get(attachmentId);
            if (offlineAttachment) {
              const file = new File([offlineAttachment.file], offlineAttachment.originalName, {
                type: offlineAttachment.mimeType
              });
              const formData = new FormData();
              formData.append('file', file);
              formData.append('type', 'equipments');

              const response = await $fetch<{ success: boolean; url: string }>('/api/upload', {
                method: 'POST',
                body: formData
              });

              if (response && response.success) {
                payloadData.image = response.url;
                await db.offlineAttachments.update(offlineAttachment.id, {
                  uploaded: true,
                  uploadedUrl: response.url,
                  updatedAt: Date.now()
                });
                await db.syncQueue.update(task.id, {
                  payload: { data: payloadData },
                  updatedAt: Date.now()
                });
              } else {
                throw new Error('Falha no upload da imagem do equipamento');
              }
            }
          }

          let endpoint = '';
          let method: 'POST' | 'PUT' | 'DELETE' = 'POST';

          if (task.type.includes('equipment')) endpoint = '/api/equipments';
          if (task.type.includes('local')) endpoint = '/api/locals';

          if (task.type.includes('create')) method = 'POST';
          if (task.type.includes('update')) method = 'PUT';
          if (task.type.includes('delete')) method = 'DELETE';

          const response = await $fetch(endpoint, {
            method,
            body: payloadData
          });

          if (response) {
            await db.syncQueue.update(task.id, { status: 'synced', updatedAt: Date.now() });
          } else {
            throw new Error('API returned empty response');
          }
        } catch (error: unknown) {
          // If the server returned 403 Forbidden, we do not retry this task anymore
          if (
            typeof error === 'object' &&
            error !== null &&
            'response' in error &&
            (error as { response: { status: number } }).response?.status === 403
          ) {
            console.error(`Permissão negada (403) ao sincronizar ${task.type}. Removendo da fila.`, error);
            await db.syncQueue.update(task.id, {
              status: 'error',
              error: 'Permissão Negada',
              retries: 999, // Prevent further retries
              updatedAt: Date.now()
            });
            continue;
          }

          console.error(`Erro ao sincronizar ação ${task.type} offline:`, error);
          await db.syncQueue.update(task.id, {
            status: 'error',
            error: error instanceof Error ? error.message : 'Unknown error',
            retries: task.retries + 1,
            updatedAt: Date.now()
          });
        }
      }
    } finally {
      this.isSyncing = false;
    }
  }
}

export const entitySyncService = new EntitySyncService();
